import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { User, Mail, Briefcase, Calendar, DollarSign, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Layout><div className="text-center py-8">Loading...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="profile-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">My Profile</h1>
          <p className="text-muted-foreground mt-2">View your personal information</p>
        </div>

        <Card data-testid="profile-card">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <User className="mr-2 h-4 w-4" />
                  Full Name
                </Label>
                <p className="text-lg font-medium">{profile?.full_name}</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <Mail className="mr-2 h-4 w-4" />
                  Email
                </Label>
                <p className="text-lg font-medium">{profile?.email}</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <Briefcase className="mr-2 h-4 w-4" />
                  Designation
                </Label>
                <p className="text-lg font-medium">{profile?.designation || 'N/A'}</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <Briefcase className="mr-2 h-4 w-4" />
                  Department
                </Label>
                <p className="text-lg font-medium">{profile?.department || 'N/A'}</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <Calendar className="mr-2 h-4 w-4" />
                  Date of Joining
                </Label>
                <p className="text-lg font-medium">{profile?.date_of_joining || 'N/A'}</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <DollarSign className="mr-2 h-4 w-4" />
                  Monthly Salary
                </Label>
                <p className="text-lg font-medium">₹{profile?.salary?.toFixed(2) || '0.00'}</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <Clock className="mr-2 h-4 w-4" />
                  Weekly Off
                </Label>
                <p className="text-lg font-medium">
                  {Array.isArray(profile?.weekly_off) 
                    ? profile.weekly_off.join(', ') 
                    : profile?.weekly_off || 'Sunday'}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center text-sm font-medium text-muted-foreground">
                  <Calendar className="mr-2 h-4 w-4" />
                  Paid Leaves Remaining
                </Label>
                <p className="text-lg font-medium">{profile?.paid_leaves_remaining || 0} days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
